import {useTheme} from '@mui/material';
import {format, parse, subMonths} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {BillStatusEnum, DateFormatEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {translations} from 'presentation/translations-new';
import {formatDateString, formatPriceBill} from 'presentation/utils';

import {useEffect, useState} from 'react';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useSelectedBilletStore} from '../../../../store/selectedBilletStore';
import {useGetObligationPdfQuery} from '../../queries/useGetObligationPdfQuery';
import {usePostInvoicePdfMutation} from '../../queries/usePostInvoicePdfMutation';
import {usePostInvoicesRecoverMutation} from '../../queries/usePostInvoicesRecoverMutation';
import {StatusConfigProps} from '../../types';
import {UseBilletDetailsProps} from './types';

const {currencyUnit} = translations['pt-br'].commons;
const {billStatus, inNegotiationPhone} = translations['pt-br'].privateFlow.myFinancial;

const useBilletDetails = ({downloadLink}: UseBilletDetailsProps = {}) => {
	const {palette} = useTheme();
	const {dataHostNameState, isGndiSaoPaulo} = useHostName();
	const {beneficiary, beneficiariesLength} = useAccountDataStore(store => store.accountData);
	const selectedCurrentBillet = useSelectedBilletStore(state => state.selectedBillet);

	const {mutateAsync: postInvoicesRecover} = usePostInvoicesRecoverMutation();
	const {mutateAsync: postInvoicePdf} = usePostInvoicePdfMutation();

	const [isDownloading, setIsDownloading] = useState(false);
	const [modalErrorState, setModalErrorState] = useState(false);
	const [selectedObligation, setSelectedObligation] = useState<number | null>(null);
	const [shouldOpenPdf, setShouldOpenPdf] = useState(false);

	const {
		cdObrigacao,
		cdObrigacaoOrigem,
		dtPrevista,
		flMigracao,
		flTipoPdf,
		linhaDigitavel,
		status,
		urlBoletoMigrado,
		dtCompInicio,
		dtCompFim,
		cdEmpresaParceira,
	} = selectedCurrentBillet ?? {};

	const isInNegotiation = status === BillStatusEnum.IN_NEGOTIATION;
	const isExpired = status === BillStatusEnum.EXPIRED;
	const isPaid = status === BillStatusEnum.PAID;
	const formattedTargetDate = dtPrevista
		? formatDateString(dtPrevista, DateFormatEnum.DATE_FORMAT_8, DateFormatEnum.DATE_FORMAT_11)
		: '';
	const price = linhaDigitavel?.vlObrigacao
		? formatPriceBill(linhaDigitavel?.vlObrigacao)
		: `${currencyUnit} -`;
	const formattedPrice = isPaid ? `${currencyUnit} ${linhaDigitavel?.vlObrigacao || '-'}` : price;
	const showBarCodeButton =
		!!linhaDigitavel && !!linhaDigitavel?.cdBarra && flTipoPdf && !flMigracao;
	const showAlertErrorCodeBar = linhaDigitavel && !linhaDigitavel?.cdBarra && !flTipoPdf;

	const EXIST_COMPETENCE_DATE = dtCompInicio && dtCompFim;
	const calcCompetenceDate =
		linhaDigitavel && linhaDigitavel?.dtVencimento
			? `${linhaDigitavel?.dtVencimento} à ${format(
					subMonths(
						parse(linhaDigitavel?.dtVencimento, 'dd/MM/yyyy', new Date(), {
							locale: ptBR,
						}),
						-1,
					),
					'dd/MM/yyyy',
				)}`
			: '-';

	const competenceOfThisMonthlyFee = EXIST_COMPETENCE_DATE
		? `${dtCompInicio} à ${dtCompFim}`
		: calcCompetenceDate;

	const handleDownload = async () => {
		if (!isDownloading) {
			try {
				setIsDownloading(true);
				if (downloadLink) {
					window.open(downloadLink);
					return;
				}
				const cdEmpresaParceiraExists = cdEmpresaParceira && cdEmpresaParceira !== null;
				const isGndiSpMigrationCompany =
					isGndiSaoPaulo && flMigracao && cdEmpresaParceiraExists;
				if (isGndiSpMigrationCompany) {
					const {boletos} = await postInvoicesRecover({
						cabecalho: {
							canal: 'M',
							codigoEmpresaOrigem: '1',
							divisaoNegocio: '1',
							empresaOrigemAtendimento: '1',
							origemContrato: '1',
							tipoContrato: '1',
							token: '',
							unidadeNegocio: '1',
						},
						carteirinha: cdEmpresaParceira.toString(),
						idBeneficiario: '',
						idTitular: '',
						status: '',
						tipoBoleto: '',
					});
					const boleto = boletos.find(
						bill => bill.idBoleto === cdObrigacaoOrigem?.toString(),
					);

					if (!boleto) {
						throw new Error('Boleto não encontrado.');
					}

					const {numeroDocumento, competencia, idBoleto} = boleto;
					const response = await postInvoicePdf({
						idBeneficiario: '',
						carteirinha: cdEmpresaParceira.toString(),
						numeroDocumento,
						cabecalho: {
							empresaOrigemAtendimento: '1',
							divisaoNegocio: '1',
							unidadeNegocio: '1',
							canal: 'M',
							origemContrato: '1',
							tipoContrato: '1',
							codigoEmpresaOrigem: '1',
							token: '1',
						},
						idTitular: '',
						mesReferencia: competencia,
						idBoleto,
					});
					window.open(URL.createObjectURL(response));
					return;
				}

				if (!flMigracao && cdObrigacao) {
					setSelectedObligation(cdObrigacao);
					setShouldOpenPdf(true);
					return;
				}
				if (urlBoletoMigrado) {
					window.open(urlBoletoMigrado);
					return;
				}

				if (cdObrigacao) {
					setSelectedObligation(cdObrigacao);
				}
				setShouldOpenPdf(true);
			} catch (error) {
				setModalErrorState(true);
			} finally {
				setIsDownloading(false);
			}
		}
	};

	const statusConfig: Record<BillStatusEnum, StatusConfigProps> = {
		[BillStatusEnum.OPEN]: {
			chipType: 'info',
			color: palette.primary.main,
			textColor: palette.text.primary,
			chipText: billStatus.open,
		},
		[BillStatusEnum.EXPIRED]: {
			chipType: 'warning',
			color: palette.secondary.main,
			textColor: palette.text.primary,
			chipText: billStatus.expired,
		},
		[BillStatusEnum.IN_NEGOTIATION]: {
			chipType: 'error',
			color: palette.error.main,
			textColor: palette.text.primary,
			chipText: billStatus.inNegotiation,
		},
		[BillStatusEnum.PAID]: {
			chipType: 'success',
			color: palette.success.main,
			textColor: palette.text.primary,
			chipText: billStatus.paid,
		},
	};

	const contactPhones = inNegotiationPhone[dataHostNameState.product];

	const {chipType, color, chipText, textColor} = statusConfig[status ?? BillStatusEnum.OPEN];

	const {
		data: pdfData,
		isLoading: isLoadingPdf,
		isFetching: isFetchingPdf,
		isError: isErrorPdf,
	} = useGetObligationPdfQuery({
		obrigacao: selectedObligation!,
		responseType: 'blob',
	});

	useEffect(() => {
		if (pdfData && shouldOpenPdf) {
			window.open(URL.createObjectURL(pdfData as Blob));
			setShouldOpenPdf(false);
		}
		if (isErrorPdf) {
			setModalErrorState(true);
			setShouldOpenPdf(false);
			setSelectedObligation(null);
		}
	}, [pdfData, shouldOpenPdf, isErrorPdf]);

	return {
		isDownloading: isDownloading || isLoadingPdf || isFetchingPdf,
		modalErrorState,
		setModalErrorState,
		beneficiary,
		beneficiariesLength,
		isInNegotiation,
		isExpired,
		isPaid,
		formattedTargetDate,
		formattedPrice,
		showBarCodeButton,
		showAlertErrorCodeBar,
		competenceOfThisMonthlyFee,
		handleDownload,
		contactPhones,
		chipType,
		color,
		chipText,
		textColor,
		linhaDigitavel,
	};
};

export {useBilletDetails};
